import React, { useEffect, useState } from "react";
import { Supervisor } from "../../../interfaces/V2Interface";
import "./V2BatchExport.css";
import Colors from '../../../styles/Colors.json';
import { useSiteContext } from "../../../SiteContext";
import PageWrapper from "../../../components/PageWrapper";
import SearchBar from "../../../components/SearchBar";
import V2BatchExportModal from "../../../components/V2BatchExportModal";
import { getSupervisorsForPayPeriod } from "../../../api/v2BatchExport";

// Define the props interface
interface V2BatchExportProps {
  setError: (error: string) => void;
}

  // Add to your interfaces file (or at the top of your component)
interface SupervisorsResponse {
  supervisors: Supervisor[];
}
  
  const V2BatchExport: React.FC<V2BatchExportProps> = ({ setError }) => {
  const {
    payPeriodID,
    startDate,
    endDate,
    translator,
    setLoading,
    createPopup,
  } = useSiteContext();

  // State for supervisors and search term
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSupervisorName, setSelectedSupervisorName] = useState("");
  const [selectedSupervisorId, setSelectedSupervisorId] = useState("");
  const [selectedSupervisorAltId, setSelectedSupervisorAltId] = useState("");

    // When export button clicked
    const handleExportClick = (supervisor: Supervisor) => {
    setSelectedSupervisorName(`${supervisor.name.last}, ${supervisor.name.first}`);
    setSelectedSupervisorId(supervisor.employeeID.toString());
    setSelectedSupervisorAltId(supervisor.altEmployeeID);
    setModalVisible(true);
  };  


  // Fetch supervisors for the current date range
  const fetchSupervisors = async () => {
    try {
      setLoading(true);
      const response = await getSupervisorsForPayPeriod(payPeriodID);
      
      // Handle both possible response formats:
      // 1. Direct array of supervisors
      // 2. { supervisors: [] } wrapper object
      const supervisorsArray = Array.isArray(response) 
        ? response 
        : (response as SupervisorsResponse)?.supervisors || [];
      
      if (!Array.isArray(supervisorsArray)) {
        console.error("Invalid supervisors data:", response);
        setSupervisors([]);
        setError("Invalid data format received from server");
        return;
      }
      
      setSupervisors(supervisorsArray);
    } catch (error) {
      console.error("Error fetching supervisors:", error);
      setError("Failed to fetch supervisors. Please try again later.");
      setSupervisors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (payPeriodID) {
      fetchSupervisors();
    }
  }, [payPeriodID]);


  // Filter supervisors based on search term
  const filteredSupervisors = Array.isArray(supervisors) 
    ? supervisors.filter(supervisor => {
        if (!supervisor) return false;
        
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${supervisor.name.first} ${supervisor.name.last}`.toLowerCase();
        const id = supervisor.altEmployeeID?.toLowerCase() || 
                  supervisor.employeeID?.toString().toLowerCase() || '';
        
        return (
          fullName.includes(searchLower) ||
          id.includes(searchLower)
        );
      })
    : [];

  return (
    <>
      <PageWrapper 
        title={translator.translate("v2BatchExport")} 
        backgroundColor={Colors.White} 
        backArrow={true}
        weekSelector={true}
      >
        <div className="batch-container">
          <SearchBar
            placeholder={"Search Supervisors"}
            value={searchTerm}
            onChange={setSearchTerm}
            style={{ width: '75%', marginBottom: '20px' }}
          />
  
          <div className="batch-list">
            {filteredSupervisors.length > 0 ? (
              filteredSupervisors.map(supervisor => (
                <div 
                  key={supervisor.altEmployeeID || supervisor.employeeID}
                  className="batch-card"
                >
                  <div className="batch-info">
                    <h4>{`${supervisor.name.last}, ${supervisor.name.first}`}</h4>
                    <p>ID: {supervisor.altEmployeeID || supervisor.employeeID}</p>
                    <p>Employees: {supervisor.count}</p>
                  </div>
                  <div className="batch-actions">
                    <button 
                      className="batch-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportClick(supervisor);
                      }}
                    >
                      Export
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="batch-no-supervisors">{"No Supervisors Found"}</p>
            )}
          </div>
        </div>
      </PageWrapper>
  
      <V2BatchExportModal 
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        supervisorName={selectedSupervisorName}
        supervisorId={selectedSupervisorId}
        supervisorAltId={selectedSupervisorAltId}
        startDate={startDate}
        endDate={endDate}
        createPopup={createPopup}
      />
    </>
  );
};

export default V2BatchExport;